import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { localVendorsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, Search, RefreshCw, Truck, Edit, Trash2, 
  Save, Phone, Mail, CreditCard, IndianRupee, Eye
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useNavigate } from 'react-router-dom';

const initialFormData = {
  name: '',
  phone: '',
  email: '',
  gstin: '',
  pan: '',
  contact_person: '',
  payment_terms_days: 30,
  credit_limit: 0,
  bank_name: '',
  bank_account: '',
  bank_ifsc: '',
  upi_id: '',
  address: {
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: ''
  }
};

const LocalVendors = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  const storeId = user?.entity_id;
  const storeType = user?.store_type || 'owned';
  const isFranchise = storeType === 'franchise';

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (search) params.search = search;
      
      const response = await localVendorsAPI.list(params);
      setVendors(response.data.items || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast({ title: 'Error', description: 'Failed to fetch vendors', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVendors();
  };

  const openCreateDialog = () => {
    setEditingVendor(null);
    setFormData({ ...initialFormData, store_id: storeId });
    setDialogOpen(true);
  };

  const openEditDialog = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      gstin: vendor.gstin || '',
      pan: vendor.pan || '',
      contact_person: vendor.contact_person || '',
      payment_terms_days: vendor.payment_terms_days || 30,
      credit_limit: vendor.credit_limit || 0,
      bank_name: vendor.bank_name || '',
      bank_account: vendor.bank_account || '',
      bank_ifsc: vendor.bank_ifsc || '',
      upi_id: vendor.upi_id || '',
      address: vendor.address || initialFormData.address
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.phone) {
      toast({ title: 'Error', description: 'Name and Phone are required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (editingVendor) {
        await localVendorsAPI.update(editingVendor.id, formData);
        toast({ title: 'Success', description: 'Vendor updated successfully' });
      } else {
        await localVendorsAPI.create({ ...formData, store_id: storeId });
        toast({ title: 'Success', description: 'Vendor created successfully' });
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (error) {
      console.error('Error saving vendor:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save vendor', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!vendorToDelete) return;
    
    try {
      await localVendorsAPI.delete(vendorToDelete.id);
      toast({ title: 'Success', description: 'Vendor deactivated' });
      setDeleteDialogOpen(false);
      fetchVendors();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete vendor', 
        variant: 'destructive' 
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="p-6 space-y-6" data-testid="local-vendors-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Local Vendors
          </h1>
          <p className="text-gray-500 mt-1">
            {isFranchise 
              ? 'Manage your local suppliers for direct purchases'
              : 'View local vendors assigned by HQ'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchVendors} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isFranchise && (
            <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, phone, GSTIN..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Button type="submit">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Vendors List */}
      <Card>
        <CardHeader className="border-b bg-gray-50/50 py-3">
          <CardTitle className="text-lg">Vendors ({vendors.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Vendor</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Loading vendors...
                  </TableCell>
                </TableRow>
              ) : vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    {isFranchise 
                      ? 'No local vendors yet. Add your first vendor!'
                      : 'No local vendors assigned by HQ'}
                  </TableCell>
                </TableRow>
              ) : (
                vendors.map((vendor) => (
                  <TableRow key={vendor.id} data-testid={`vendor-row-${vendor.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{vendor.name}</p>
                        <p className="text-sm text-gray-500">{vendor.code}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <Phone className="w-3 h-3 text-gray-400" />
                          {vendor.phone}
                        </div>
                        {vendor.email && (
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail className="w-3 h-3 text-gray-400" />
                            {vendor.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{vendor.gstin || '-'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={vendor.current_balance > 0 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                        {formatCurrency(vendor.current_balance)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={vendor.vendor_type === 'franchise' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'}>
                        {vendor.vendor_type === 'franchise' ? 'Own Vendor' : 'HQ Assigned'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/store/local-vendors/${vendor.id}/ledger`)}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Ledger
                        </Button>
                        {(isFranchise && vendor.vendor_type === 'franchise') && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openEditDialog(vendor)}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setVendorToDelete(vendor);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              {editingVendor ? 'Edit Vendor' : 'Add Local Vendor'}
            </DialogTitle>
            <DialogDescription>
              {editingVendor ? 'Update vendor details' : 'Add a new local supplier'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Vendor name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                  placeholder="Phone number"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@vendor.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData(p => ({ ...p, contact_person: e.target.value }))}
                  placeholder="Contact person name"
                />
              </div>
            </div>

            {/* Tax Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>GSTIN</Label>
                <Input
                  value={formData.gstin}
                  onChange={(e) => setFormData(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                  placeholder="22AAAAA0000A1Z5"
                />
              </div>
              <div className="space-y-2">
                <Label>PAN</Label>
                <Input
                  value={formData.pan}
                  onChange={(e) => setFormData(p => ({ ...p, pan: e.target.value.toUpperCase() }))}
                  placeholder="ABCDE1234F"
                />
              </div>
            </div>

            {/* Payment Terms */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Terms (Days)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.payment_terms_days}
                  onChange={(e) => setFormData(p => ({ ...p, payment_terms_days: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Credit Limit (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.credit_limit}
                  onChange={(e) => setFormData(p => ({ ...p, credit_limit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            {/* Banking */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Banking Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData(p => ({ ...p, bank_name: e.target.value }))}
                    placeholder="Bank name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={formData.bank_account}
                    onChange={(e) => setFormData(p => ({ ...p, bank_account: e.target.value }))}
                    placeholder="Account number"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>IFSC Code</Label>
                  <Input
                    value={formData.bank_ifsc}
                    onChange={(e) => setFormData(p => ({ ...p, bank_ifsc: e.target.value.toUpperCase() }))}
                    placeholder="SBIN0001234"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UPI ID</Label>
                  <Input
                    value={formData.upi_id}
                    onChange={(e) => setFormData(p => ({ ...p, upi_id: e.target.value }))}
                    placeholder="vendor@upi"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Address</h4>
              <div className="space-y-4">
                <Input
                  value={formData.address?.address_line1 || ''}
                  onChange={(e) => setFormData(p => ({ 
                    ...p, 
                    address: { ...p.address, address_line1: e.target.value }
                  }))}
                  placeholder="Address Line 1"
                />
                <Input
                  value={formData.address?.address_line2 || ''}
                  onChange={(e) => setFormData(p => ({ 
                    ...p, 
                    address: { ...p.address, address_line2: e.target.value }
                  }))}
                  placeholder="Address Line 2"
                />
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    value={formData.address?.city || ''}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      address: { ...p.address, city: e.target.value }
                    }))}
                    placeholder="City"
                  />
                  <Input
                    value={formData.address?.state || ''}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      address: { ...p.address, state: e.target.value }
                    }))}
                    placeholder="State"
                  />
                  <Input
                    value={formData.address?.pincode || ''}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      address: { ...p.address, pincode: e.target.value }
                    }))}
                    placeholder="Pincode"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingVendor ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Deactivate Vendor</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate "{vendorToDelete?.name}"? 
              This vendor will no longer appear in your list.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalVendors;
